/**
 * 🐾 Sovereign Notification Service for Fissal Mobile
 * Bridges native Android notification channels (@capacitor/local-notifications),
 * custom FissalNotification rich BigPicture heads-up alerts with live camera snapshots,
 * per-topic cooldown throttles, sound/vibration toggles, and recent alert history.
 */

import { LocalNotifications } from '@capacitor/local-notifications';
import { registerPlugin } from '@capacitor/core';
import { triggerHaptic } from './purrSynthesizer';
import { Preferences } from '@capacitor/preferences';
import { connectionManager } from './connectionManager';
import { getStoredToken } from './secureStorage';

export interface FissalNotificationPlugin {
  showRichNotification(options: {
    id?: number;
    channelId?: string;
    title: string;
    body: string;
    imageUrl?: string;
    summary?: string;
    smallIcon?: string;
    extra?: Record<string, any>;
  }): Promise<{ success: boolean }>;

  updateServerConfig(options: {
    serverUrl?: string;
    gatewayUrl: string;
    token?: string;
  }): Promise<{ success: boolean }>;
}

export const FissalNotification = registerPlugin<FissalNotificationPlugin>('FissalNotification');

export type NotificationTopic = 
  | 'camera_detections'
  | 'trader_timers'
  | 'eso_servers'
  | 'investing_sentinel'
  | 'system_health';

export interface TopicConfig {
  enabled: boolean;
  cooldownMinutes: number; // 0 = instant, 1, 5, 15, 30, 60, 360
  sound: boolean;
  vibration: boolean;
  displayName: string;
  description: string;
  icon: string;
  channelId: string;
}

export interface PushNotificationPayload {
  id: number;
  topic: NotificationTopic;
  title: string;
  body: string;
  icon?: string;
  timestamp: number;
  data?: Record<string, any>;
}

export interface NotificationPreferences {
  masterEnabled: boolean;
  topics: Record<NotificationTopic, TopicConfig>;
  lastNotified: Partial<Record<NotificationTopic, number>>;
}

const STORAGE_KEY_NOTIF_PREFS = 'fissal_notification_preferences';
const STORAGE_KEY_NOTIF_HISTORY = 'fissal_notification_history';

export const DEFAULT_TOPIC_CONFIGS: Record<NotificationTopic, TopicConfig> = {
  camera_detections: {
    enabled: true,
    cooldownMinutes: 5,
    sound: true,
    vibration: true,
    displayName: 'Perimeter Cameras',
    description: 'Frigate NVR person, vehicle, and pet movement detections',
    icon: '📹',
    channelId: 'camera_detections'
  },
  trader_timers: {
    enabled: true,
    cooldownMinutes: 60,
    sound: true,
    vibration: true,
    displayName: 'ESO Guild Traders',
    description: 'Weekly Tuesday 19:00 UTC trader flip and bid countdowns',
    icon: '💰',
    channelId: 'trader_timers'
  },
  eso_servers: {
    enabled: true,
    cooldownMinutes: 15,
    sound: true,
    vibration: true,
    displayName: 'ESO Megaservers',
    description: 'PC-NA / PC-EU realm maintenance, downtime, and recovery alerts',
    icon: '⚔️',
    channelId: 'eso_servers'
  },
  investing_sentinel: {
    enabled: true,
    cooldownMinutes: 0,
    sound: true,
    vibration: true,
    displayName: 'Sentinel Investing',
    description: 'Autonomous trade executions, stop-loss triggers, and chaos spikes',
    icon: '📈',
    channelId: 'investing_sentinel'
  },
  system_health: {
    enabled: true,
    cooldownMinutes: 30,
    sound: true,
    vibration: true,
    displayName: 'Homelab Fleet Vitals',
    description: 'Daemon restart loops, excessive memory bloat, and disk thresholds',
    icon: '🛡️',
    channelId: 'system_health'
  }
};

class NotificationService {
  private prefs: NotificationPreferences;
  private history: PushNotificationPayload[] = [];
  private channelsInitialized = false;
  private listeners: Set<(prefs: NotificationPreferences) => void> = new Set();
  private historyListeners: Set<(history: PushNotificationPayload[]) => void> = new Set();

  constructor() {
    this.prefs = {
      masterEnabled: true,
      topics: { ...DEFAULT_TOPIC_CONFIGS },
      lastNotified: {}
    };
    this.loadFromStorage();

    // Auto-sync active gateway URL to native background relay
    try {
      connectionManager.subscribe(() => {
        this.syncServerConfig();
      });
    } catch {}

    // Attach listener for notification tap actions (fallback when LocalNotifications is used)
    try {
      LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
        console.log('🐾 [NotificationService] Local notification action received:', action);
        const extra = action.notification?.extra;
        if (extra?.camera || extra?.topic) {
          window.dispatchEvent(
            new CustomEvent('fissal:notification_clicked', {
              detail: {
                camera: extra.camera,
                topic: extra.topic,
                data: extra
              }
            })
          );
        }
      });
    } catch {}
  }

  public async syncServerConfig(): Promise<void> {
    try {
      const gatewayUrl = connectionManager.getActiveUrl();
      const token = getStoredToken() || '';
      if (gatewayUrl && FissalNotification && typeof FissalNotification.updateServerConfig === 'function') {
        await FissalNotification.updateServerConfig({ serverUrl: gatewayUrl, gatewayUrl, token });
        console.log(`🐾 [NotificationService] Background relay gateway synced: ${gatewayUrl}`);
      }
    } catch {
      // Ignored on web/desktop
    }
  }

  private async loadFromStorage() {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const raw = localStorage.getItem(STORAGE_KEY_NOTIF_PREFS);
        if (raw) {
          const parsed = JSON.parse(raw);
          this.prefs = {
            masterEnabled: parsed.masterEnabled ?? true,
            topics: { ...DEFAULT_TOPIC_CONFIGS, ...(parsed.topics || {}) },
            lastNotified: parsed.lastNotified || {}
          };
        }
        const histRaw = localStorage.getItem(STORAGE_KEY_NOTIF_HISTORY);
        if (histRaw) {
          this.history = JSON.parse(histRaw);
        }
      }

      // Sync from native Preferences
      const nativePrefs = await Preferences.get({ key: STORAGE_KEY_NOTIF_PREFS });
      if (nativePrefs.value) {
        try {
          const parsed = JSON.parse(nativePrefs.value);
          this.prefs = {
            masterEnabled: parsed.masterEnabled ?? true,
            topics: { ...DEFAULT_TOPIC_CONFIGS, ...(parsed.topics || {}) },
            lastNotified: parsed.lastNotified || {}
          };
        } catch {}
      }
    } catch (err) {
      console.warn('[NotificationService] Load storage warning:', err);
    }
  }

  private async savePreferences() {
    try {
      const serialized = JSON.stringify(this.prefs);
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(STORAGE_KEY_NOTIF_PREFS, serialized);
      }
      await Preferences.set({ key: STORAGE_KEY_NOTIF_PREFS, value: serialized });
    } catch (err) {
      console.warn('[NotificationService] Save prefs warning:', err);
    }
    this.notifyListeners();
  }

  private saveHistory() {
    try {
      const slice = this.history.slice(0, 50);
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(STORAGE_KEY_NOTIF_HISTORY, JSON.stringify(slice));
      }
    } catch {}
    this.notifyHistoryListeners();
  }

  /**
   * Initializes dedicated Android Notification Channels
   */
  public async initChannels(): Promise<boolean> {
    if (this.channelsInitialized) return true;

    try {
      // Check or request notification permission
      const perm = await LocalNotifications.checkPermissions();
      if (perm.display !== 'granted') {
        const req = await LocalNotifications.requestPermissions();
        if (req.display !== 'granted') {
          console.warn('🐾 [NotificationService] Notification permission not granted by user.');
        }
      }

      // Create Android 8.0+ channels with distinct importance
      await Promise.all([
        LocalNotifications.createChannel({
          id: 'camera_detections',
          name: 'Perimeter & Cameras 📹',
          description: 'High-priority camera detections (person, vehicle, pets)',
          importance: 4, // High: Heads-up banner + sound
          visibility: 1,
          vibration: true
        }),
        LocalNotifications.createChannel({
          id: 'trader_timers',
          name: 'ESO Guild Traders 💰',
          description: 'Weekly Tuesday trader flips & bid deadline countdowns',
          importance: 3, // Default
          visibility: 1,
          vibration: true
        }),
        LocalNotifications.createChannel({
          id: 'eso_servers',
          name: 'ESO Megaservers ⚔️',
          description: 'NA and EU Megaserver maintenance and downtime notices',
          importance: 4, // High
          visibility: 1,
          vibration: true
        }),
        LocalNotifications.createChannel({
          id: 'investing_sentinel',
          name: 'Sentinel Investing 📈',
          description: 'Autonomous trades, stop-losses, and market volatility radar',
          importance: 4, // High
          visibility: 1,
          vibration: true
        }),
        LocalNotifications.createChannel({
          id: 'system_health',
          name: 'Homelab Fleet Vitals 🛡️',
          description: 'Critical system warnings, process loops, and hardware health',
          importance: 4, // High
          visibility: 1,
          vibration: true
        })
      ]);

      this.channelsInitialized = true;
      console.log('🐾 [NotificationService] Android Notification Channels successfully configured!');
      
      // Sync gateway config with native relay
      await this.syncServerConfig();
      return true;
    } catch (err: any) {
      console.warn('[NotificationService] Channel init warning (expected in desktop browser):', err.message);
      return false;
    }
  }

  /**
   * Incoming notification evaluation: Checks master toggle, topic enabled state, and cooldown duration
   */
  public async handleIncomingNotification(payload: PushNotificationPayload): Promise<boolean> {
    if (!this.prefs.masterEnabled) {
      console.log('🐾 [NotificationService] Notification skipped: Master switch is OFF.');
      return false;
    }

    const topicCfg = this.prefs.topics[payload.topic] || DEFAULT_TOPIC_CONFIGS[payload.topic];
    if (!topicCfg || !topicCfg.enabled) {
      console.log(`🐾 [NotificationService] Notification skipped: Topic [${payload.topic}] is MUTED.`);
      return false;
    }

    // Cooldown verification
    const cooldownMs = (topicCfg.cooldownMinutes || 0) * 60 * 1000;
    const lastTime = this.prefs.lastNotified[payload.topic] || 0;
    const timeSinceLast = Date.now() - lastTime;

    if (cooldownMs > 0 && timeSinceLast < cooldownMs) {
      const remainingSecs = Math.round((cooldownMs - timeSinceLast) / 1000);
      console.log(`🐾 [NotificationService] Topic [${payload.topic}] throttled by cooldown (${remainingSecs}s remaining).`);
      return false;
    }

    // Update last notified time
    this.prefs.lastNotified[payload.topic] = Date.now();
    this.savePreferences();

    // Record in history log
    this.history.unshift(payload);
    if (this.history.length > 50) this.history = this.history.slice(0, 50);
    this.saveHistory();

    // Dispatch Native Android Notification
    try {
      await this.initChannels();

      const notifId = Math.abs(payload.id % 2147483647);

      // Resolve full image URL for rich attachments
      let resolvedImageUrl: string | undefined;
      const rawImage = payload.data?.image_url || payload.data?.snapshot_url || payload.data?.imageUrl || payload.data?.snapshotUrl;
      if (rawImage && typeof rawImage === 'string') {
        if (rawImage.startsWith('http://') || rawImage.startsWith('https://')) {
          resolvedImageUrl = rawImage;
        } else {
          resolvedImageUrl = `${connectionManager.getActiveUrl()}${rawImage.startsWith('/') ? '' : '/'}${rawImage}`;
        }
      }

      const summaryText = payload.data?.summary || payload.data?.ai_summary || payload.body;

      const extraPayload = {
        ...(payload.data || {}),
        topic: payload.topic,
        camera: payload.data?.camera,
        eventId: payload.data?.eventId || payload.data?.id
      };

      try {
        await FissalNotification.showRichNotification({
          id: notifId,
          channelId: topicCfg.channelId || payload.topic,
          title: payload.title,
          body: payload.body,
          imageUrl: resolvedImageUrl,
          summary: summaryText,
          extra: extraPayload
        });
        console.log(`🐾 [NotificationService] Native Rich Notification posted: "${payload.title}" (image: ${resolvedImageUrl || 'none'}, cam: ${extraPayload.camera || 'none'})`);
      } catch (nativeErr: any) {
        console.warn('[NotificationService] Native plugin fallback to LocalNotifications:', nativeErr?.message);
        // Direct immediate LocalNotification without exact alarm schedule
        await LocalNotifications.schedule({
          notifications: [
            {
              id: notifId,
              title: payload.title,
              body: payload.body,
              channelId: topicCfg.channelId || payload.topic,
              extra: extraPayload,
              smallIcon: 'ic_launcher_foreground'
            }
          ]
        });
      }

      if (topicCfg.vibration) {
        triggerHaptic([40, 80, 40]);
      }

      return true;
    } catch (err: any) {
      console.warn('[NotificationService] Notification dispatch error:', err.message);
      return false;
    }
  }

  /**
   * Fires an immediate test notification for any topic
   */
  public async testNotification(topic: NotificationTopic): Promise<boolean> {
    const topicCfg = this.prefs.topics[topic] || DEFAULT_TOPIC_CONFIGS[topic];
    const testPayloads: Record<NotificationTopic, PushNotificationPayload> = {
      camera_detections: {
        id: Date.now(),
        topic: 'camera_detections',
        title: 'Dogshop Porch: Person Detected 🚶',
        body: 'Echo is seen stepping onto the porch with work tools. Whiskers note safe arrival.',
        icon: '🚶',
        timestamp: Date.now(),
        data: {
          camera: 'e-roto_1',
          image_url: '/api/frigate/snapshot/e-roto_1',
          summary: 'Echo is seen stepping onto the porch with work tools. Whiskers note safe arrival.'
        }
      },
      trader_timers: {
        id: Date.now(),
        topic: 'trader_timers',
        title: 'ESO Guild Trader: Reset Flip in 2 Hours! 💰',
        body: 'Tamriel United & Redfur bids finalize at 19:00 UTC. Check council reserves.',
        icon: '💰',
        timestamp: Date.now()
      },
      eso_servers: {
        id: Date.now(),
        topic: 'eso_servers',
        title: 'ESO Megaservers: PC-NA is UP ⚔️',
        body: 'All North American Megaservers are online with normal ping. Maintenance complete.',
        icon: '⚔️',
        timestamp: Date.now()
      },
      investing_sentinel: {
        id: Date.now(),
        topic: 'investing_sentinel',
        title: 'Fissal Sentinel: Trade Executed 📈',
        body: 'Autonomous investment council rebalanced tactical equity reserve (+1.4% profit target).',
        icon: '📈',
        timestamp: Date.now()
      },
      system_health: {
        id: Date.now(),
        topic: 'system_health',
        title: 'Homelab Fleet: All 6 Daemons Green 🛡️',
        body: 'Fissal Gateway, Sentinel, Dashboard, and RedfurBot are humming with 0% dropped links.',
        icon: '🛡️',
        timestamp: Date.now()
      }
    };

    const payload = testPayloads[topic] || testPayloads.camera_detections;
    
    // Bypass cooldown for explicit user test
    this.history.unshift(payload);
    this.saveHistory();

    await this.initChannels();
    const notifId = Math.abs(payload.id % 2147483647);

    // Resolve full image URL for rich attachments
    let resolvedImageUrl: string | undefined;
    const rawImage = payload.data?.image_url || payload.data?.snapshot_url;
    if (rawImage && typeof rawImage === 'string') {
      if (rawImage.startsWith('http://') || rawImage.startsWith('https://')) {
        resolvedImageUrl = rawImage;
      } else {
        resolvedImageUrl = `${connectionManager.getActiveUrl()}${rawImage.startsWith('/') ? '' : '/'}${rawImage}`;
      }
    }

    const testExtra = {
      ...(payload.data || {}),
      topic: payload.topic,
      camera: payload.data?.camera,
      eventId: payload.data?.eventId || payload.data?.id
    };

    try {
      await FissalNotification.showRichNotification({
        id: notifId,
        channelId: topicCfg.channelId || topic,
        title: `[TEST] ${payload.title}`,
        body: payload.body,
        imageUrl: resolvedImageUrl,
        summary: payload.data?.summary || payload.body,
        extra: testExtra
      });
      console.log(`🐾 [NotificationService] Native Rich Test Notification posted: "${payload.title}" (cam: ${testExtra.camera || 'none'})`);
    } catch (pluginErr: any) {
      await LocalNotifications.schedule({
        notifications: [
          {
            id: notifId,
            title: `[TEST] ${payload.title}`,
            body: payload.body,
            channelId: topicCfg.channelId || topic,
            extra: testExtra,
            smallIcon: 'ic_launcher_foreground'
          }
        ]
      });
    }

    triggerHaptic([50, 100, 50]);
    return true;
  }

  // Getters & Setters
  public getPreferences(): NotificationPreferences {
    return { ...this.prefs };
  }

  public getHistory(): PushNotificationPayload[] {
    return [...this.history];
  }

  public setMasterEnabled(enabled: boolean) {
    this.prefs.masterEnabled = enabled;
    this.savePreferences();
  }

  public updateTopic(topic: NotificationTopic, updates: Partial<TopicConfig>) {
    if (!this.prefs.topics[topic]) {
      this.prefs.topics[topic] = { ...DEFAULT_TOPIC_CONFIGS[topic] };
    }
    this.prefs.topics[topic] = {
      ...this.prefs.topics[topic],
      ...updates
    };
    this.savePreferences();
  }

  public clearCooldown(topic: NotificationTopic) {
    delete this.prefs.lastNotified[topic];
    this.savePreferences();
  }

  public resetCooldown(topic: NotificationTopic) {
    this.clearCooldown(topic);
  }

  public toggleSound(topic: NotificationTopic) {
    const cfg = this.prefs.topics[topic] || DEFAULT_TOPIC_CONFIGS[topic];
    this.updateTopic(topic, { sound: !cfg.sound });
  }

  public toggleVibration(topic: NotificationTopic) {
    const cfg = this.prefs.topics[topic] || DEFAULT_TOPIC_CONFIGS[topic];
    this.updateTopic(topic, { vibration: !cfg.vibration });
  }

  public clearHistory() {
    this.history = [];
    this.saveHistory();
  }

  // Subscriptions
  public subscribe(listener: (prefs: NotificationPreferences) => void): () => void {
    this.listeners.add(listener);
    listener(this.getPreferences());
    return () => this.listeners.delete(listener);
  }

  public subscribeHistory(listener: (history: PushNotificationPayload[]) => void): () => void {
    this.historyListeners.add(listener);
    listener(this.getHistory());
    return () => this.historyListeners.delete(listener);
  }

  private notifyListeners() {
    const prefs = this.getPreferences();
    this.listeners.forEach(fn => fn(prefs));
  }

  private notifyHistoryListeners() {
    const hist = this.getHistory();
    this.historyListeners.forEach(fn => fn(hist));
  }
}

export const notificationService = new NotificationService();
